/**
 * WriterContractPopup — daily read-only contract reminder for
 * approved writers/scribes.
 *
 * Feb 2026 user spec: *"show read only version of contract to writers
 * once a week. pop up"*. Confirmed follow-up: all approved writers, on
 * login (first login of the day), for 30 unique days total.
 *
 * How it works
 *  - On mount (i.e. authed page load), GET /api/writer/contract-popup.
 *  - Backend gates on `role === writer/admin/king` + APPROVED contract,
 *    and returns should_show=true only if today's UTC date isn't in the
 *    seen_dates set AND days_seen < 30.
 *  - Admins/Kings ALWAYS get should_show=true (for QA preview).
 *  - User clicks "I understand" → POST /api/writer/contract-popup/ack
 *    which appends today's date and returns should_show=false.
 *
 * Design: full-screen backdrop, centered dialog with scrollable body,
 * heavy on the "read-only" cue (banner + no edit affordances).
 */
import React, { useEffect, useState } from 'react';
import { X, FileText, ShieldCheck, Info } from 'lucide-react';
import { Button } from './ui/button';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Minimal markdown renderer — just handles headings (# / ##) and
// bullet points (- / *). We don't need full CommonMark for a
// contract; anything more and we'd pull in `react-markdown`.
const renderMarkdown = (md) => {
  if (!md) return null;
  const lines = md.split('\n');
  const out = [];
  let listBuffer = [];
  const flushList = () => {
    if (listBuffer.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc pl-5 space-y-1 my-2 text-slate-200 text-sm">
          {listBuffer.map((li, i) => <li key={i}>{li}</li>)}
        </ul>,
      );
      listBuffer = [];
    }
  };
  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    if (/^\s*-\s+/.test(line) || /^\s*\*\s+/.test(line)) {
      listBuffer.push(line.replace(/^\s*[-*]\s+/, ''));
      return;
    }
    flushList();
    if (line.startsWith('# ')) {
      out.push(<h2 key={i} className="text-white text-lg font-black mt-4 mb-2">{line.slice(2)}</h2>);
    } else if (line.startsWith('## ')) {
      out.push(<h3 key={i} className="text-amber-200 text-sm font-bold uppercase tracking-wide mt-4 mb-1">{line.slice(3)}</h3>);
    } else if (!line.trim()) {
      out.push(<div key={i} className="h-2" />);
    } else if (line.startsWith('---')) {
      out.push(<hr key={i} className="border-slate-700 my-3" />);
    } else {
      // Basic bold: **text**
      const parts = line.split(/(\*\*[^*]+\*\*)/g).map((chunk, idx) => {
        if (chunk.startsWith('**') && chunk.endsWith('**')) {
          return <strong key={idx} className="text-white">{chunk.slice(2, -2)}</strong>;
        }
        return <React.Fragment key={idx}>{chunk}</React.Fragment>;
      });
      out.push(<p key={i} className="text-slate-200 text-sm leading-relaxed my-1.5">{parts}</p>);
    }
  });
  flushList();
  return out;
};

const WriterContractPopup = ({ user }) => {
  const [payload, setPayload] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reachedBottom, setReachedBottom] = useState(false);

  // Only fetch for writer-shaped roles. Skip readers to avoid the extra call.
  const roleEligible = user && ['writer', 'admin', 'king', 'coadmin'].includes((user.role || '').toLowerCase());

  useEffect(() => {
    if (!roleEligible) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/writer/contract-popup`);
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        setPayload(data);
        if (data?.should_show) setOpen(true);
      } catch {
        // Silent fail — the popup is not critical to page render.
      }
    })();
    return () => { cancelled = true; };
  }, [roleEligible]);

  const handleAck = async () => {
    setBusy(true);
    try {
      await authFetch(`${BACKEND_URL}/api/writer/contract-popup/ack`, { method: 'POST' });
    } catch {
      // Even if the ack fails we'll just close — next reload retries.
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  if (!open || !payload?.terms) return null;

  const { version, terms_markdown, updated_at } = payload.terms;
  const daysSeen = payload.days_seen ?? 0;
  const remaining = payload.shows_remaining ?? 0;
  const maxDays = payload.max_days ?? 30;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center px-4 py-6 bg-black/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="writer-contract-popup-title"
      data-testid="writer-contract-popup"
    >
      <div className="w-full max-w-2xl bg-slate-900 border-2 border-amber-500/40 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-800 bg-gradient-to-r from-amber-900/30 to-slate-900">
          <div className="flex items-start gap-3 min-w-0">
            <FileText className="w-6 h-6 text-amber-300 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <h2 id="writer-contract-popup-title" className="text-white text-base font-black">
                Your Writer / Scribe Contract
              </h2>
              <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-300 uppercase tracking-wider font-bold">
                  <ShieldCheck className="w-3 h-3" /> Read-only
                </span>
                <span>· Version {version}</span>
                {updated_at && (
                  <span className="text-slate-500">
                    · Updated {new Date(updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleAck}
            disabled={busy}
            className="text-slate-400 hover:text-white transition-colors p-1 -mr-1 disabled:opacity-40"
            data-testid="writer-contract-popup-x"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body — scrollable, read-only */}
        <div
          className="flex-1 overflow-y-auto px-5 py-4 select-text"
          data-testid="writer-contract-popup-body"
          onScroll={(e) => {
            const el = e.currentTarget;
            if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
              setReachedBottom(true);
            }
          }}
        >
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 text-[12px] text-amber-100 mb-3 flex items-start gap-2">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              This is a read-only reminder shown once per day, up to {maxDays} days total. Any edits to the contract are made by an Admin — you can't modify this text from here.
            </div>
          </div>
          {renderMarkdown(terms_markdown)}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-800 bg-slate-950/60 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[11px] text-slate-400">
            Day {Math.min(daysSeen + 1, maxDays)} / {maxDays} · {remaining > 0 ? `${remaining} reminder${remaining === 1 ? '' : 's'} left` : 'Last reminder'}
          </p>
          <Button
            onClick={handleAck}
            disabled={busy || !reachedBottom}
            className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold disabled:opacity-40"
            data-testid="writer-contract-popup-ack"
          >
            {busy ? 'Saving…' : reachedBottom ? 'I understand — dismiss for today' : 'Scroll to bottom to enable'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WriterContractPopup;
